import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  FileText,
  Save,
  Settings,
  Layout,
  Users,
  Calendar,
  Mail,
  Plus,
  X,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { ReportTemplate, ReportSection } from '@/types/reports';

interface ReportTemplateEditorProps {
  template?: ReportTemplate;
  onSave: (template: ReportTemplate) => void;
  onCancel: () => void;
}

const ReportTemplateEditor: React.FC<ReportTemplateEditorProps> = ({
  template,
  onSave,
  onCancel,
}) => {
  const [name, setName] = useState(template?.name || '');
  const [description, setDescription] = useState(template?.description || '');
  const [type, setType] = useState<ReportTemplate['type']>(template?.type || 'player');
  const [frequency, setFrequency] = useState<ReportTemplate['frequency']>(template?.frequency || 'weekly');
  const [format, setFormat] = useState<ReportTemplate['format']>(template?.format || 'pdf');
  const [sections, setSections] = useState<ReportSection[]>(template?.sections || []);
  const [recipients, setRecipients] = useState<string[]>(template?.recipients || []);
  const [includeCharts, setIncludeCharts] = useState(template?.parameters?.includeCharts ?? true);
  const [includeRecommendations, setIncludeRecommendations] = useState(
    template?.parameters?.includeRecommendations ?? true
  );
  const [newRecipient, setNewRecipient] = useState('');
  const [activeTab, setActiveTab] = useState('general');

  const handleAddSection = () => {
    setSections([
      ...sections,
      {
        id: `section-${Date.now()}`,
        title: 'New Section',
        type: 'text',
        content: '',
        order: sections.length,
        enabled: true,
      },
    ]);
  };

  const handleUpdateSection = (index: number, updates: Partial<ReportSection>) => {
    const updatedSections = [...sections];
    updatedSections[index] = { ...updatedSections[index], ...updates };
    setSections(updatedSections);
  };

  const handleRemoveSection = (index: number) => {
    setSections(sections.filter((_, i) => i !== index));
  };

  const handleMoveSection = (index: number, direction: 'up' | 'down') => {
    const newSections = [...sections];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex >= 0 && newIndex < sections.length) {
      [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
      setSections(newSections);
    }
  };

  const handleAddRecipient = () => {
    if (newRecipient && !recipients.includes(newRecipient)) {
      setRecipients([...recipients, newRecipient]);
      setNewRecipient('');
    }
  };

  const handleRemoveRecipient = (email: string) => {
    setRecipients(recipients.filter(r => r !== email));
  };

  const handleSave = () => {
    const newTemplate: ReportTemplate = {
      id: template?.id || `template-${Date.now()}`,
      name,
      description,
      type,
      frequency,
      format,
      status: 'active',
      sections,
      recipients,
      recipientCount: recipients.length,
      createdBy: 'current-user', // Would come from auth context
      createdAt: template?.createdAt || new Date().toISOString(),
      lastGenerated: template?.lastGenerated || '',
      nextScheduled: '',
      parameters: {
        includeCharts,
        includeRecommendations,
      },
    };

    onSave(newTemplate);
  };

  const isValid = name && description && sections.length > 0;

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>{template ? 'Edit Report Template' : 'Create Report Template'}</span>
            <div className="flex space-x-2">
              <Button variant="outline" onClick={onCancel}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={!isValid}>
                <Save className="w-4 h-4 mr-2" />
                Save Template
              </Button>
            </div>
          </CardTitle>
          <CardDescription>
            Design a custom report template with sections, data, and formatting
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="general">
                <Settings className="w-4 h-4 mr-2" />
                General
              </TabsTrigger>
              <TabsTrigger value="sections">
                <Layout className="w-4 h-4 mr-2" />
                Sections
              </TabsTrigger>
              <TabsTrigger value="recipients">
                <Users className="w-4 h-4 mr-2" />
                Recipients
              </TabsTrigger>
              <TabsTrigger value="schedule">
                <Calendar className="w-4 h-4 mr-2" />
                Schedule
              </TabsTrigger>
            </TabsList>

            <TabsContent value="general" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Template Name</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g., Weekly Player Progress Report"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="type">Report Type</Label>
                  <Select value={type} onValueChange={(value: ReportTemplate['type']) => setType(value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="player">Player Report</SelectItem>
                      <SelectItem value="coach">Coach Report</SelectItem>
                      <SelectItem value="academy">Academy Report</SelectItem>
                      <SelectItem value="parent">Parent Report</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe what this report includes and its purpose..."
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="format">Export Format</Label>
                  <Select value={format} onValueChange={(value: ReportTemplate['format']) => setFormat(value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pdf">PDF</SelectItem>
                      <SelectItem value="excel">Excel</SelectItem>
                      <SelectItem value="csv">CSV</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="frequency">Generation Frequency</Label>
                  <Select value={frequency} onValueChange={(value: ReportTemplate['frequency']) => setFrequency(value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Include Charts & Visualizations</Label>
                    <p className="text-sm text-muted-foreground">
                      Add data visualizations to the report
                    </p>
                  </div>
                  <Switch
                    checked={includeCharts}
                    onCheckedChange={setIncludeCharts}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Include AI Recommendations</Label>
                    <p className="text-sm text-muted-foreground">
                      Add AI-powered insights and suggestions
                    </p>
                  </div>
                  <Switch
                    checked={includeRecommendations}
                    onCheckedChange={setIncludeRecommendations}
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="sections" className="space-y-4 mt-4">
              <div className="flex justify-between items-center">
                <p className="text-sm text-muted-foreground">
                  Define the sections and content of your report
                </p>
                <Button onClick={handleAddSection} size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Section
                </Button>
              </div>

              <div className="space-y-4">
                {sections.map((section, index) => (
                  <Card key={section.id}>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <Input
                          value={section.title}
                          onChange={(e) => handleUpdateSection(index, { title: e.target.value })}
                          className="text-lg font-semibold"
                          placeholder="Section Title"
                        />
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMoveSection(index, 'up')}
                            disabled={index === 0}
                          >
                            <ChevronUp className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMoveSection(index, 'down')}
                            disabled={index === sections.length - 1}
                          >
                            <ChevronDown className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveSection(index)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label>Section Type</Label>
                          <Select
                            value={section.type}
                            onValueChange={(value) => handleUpdateSection(index, { type: value as ReportSection['type'] })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="text">Text Content</SelectItem>
                              <SelectItem value="metrics">Key Metrics</SelectItem>
                              <SelectItem value="table">Data Table</SelectItem>
                              <SelectItem value="chart">Chart/Graph</SelectItem>
                              <SelectItem value="recommendations">Recommendations</SelectItem>
                              <SelectItem value="drills">Training Drills</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="flex items-center space-x-2">
                          <Switch
                            checked={section.enabled}
                            onCheckedChange={(checked) => handleUpdateSection(index, { enabled: checked })}
                          />
                          <Label>Enabled</Label>
                        </div>
                      </div>

                      {section.type === 'text' && (
                        <div className="space-y-2">
                          <Label>Content Template</Label>
                          <Textarea
                            value={typeof section.content === 'string' ? section.content : ''}
                            onChange={(e) => handleUpdateSection(index, { content: e.target.value })}
                            placeholder="Enter section content or template with variables like {player_name}..."
                            rows={3}
                          />
                        </div>
                      )}

                      {section.type === 'metrics' && (
                        <div className="space-y-2">
                          <Label>Select Metrics</Label>
                          <div className="flex flex-wrap gap-2">
                            {['Average Score', 'Shot Accuracy', 'Consistency', 'Speed', 'Timing'].map(metric => (
                              <Badge key={metric} variant="outline" className="cursor-pointer">
                                {metric}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}

                {sections.length === 0 && (
                  <Card className="border-dashed">
                    <CardContent className="text-center py-8">
                      <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                      <p className="text-muted-foreground">
                        No sections added yet. Click "Add Section" to start building your report.
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            <TabsContent value="recipients" className="space-y-4 mt-4">
              <div className="space-y-4">
                <div className="flex space-x-2">
                  <Input
                    placeholder="Enter email address..."
                    value={newRecipient}
                    onChange={(e) => setNewRecipient(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddRecipient()}
                  />
                  <Button onClick={handleAddRecipient}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Recipient
                  </Button>
                </div>

                <div className="space-y-2">
                  {recipients.map((email) => (
                    <div key={email} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center space-x-3">
                        <Mail className="w-4 h-4 text-muted-foreground" />
                        <span>{email}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveRecipient(email)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}

                  {recipients.length === 0 && (
                    <Card className="border-dashed">
                      <CardContent className="text-center py-8">
                        <Users className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                        <p className="text-muted-foreground">
                          No recipients added yet. Add email addresses to automatically send reports.
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="schedule" className="space-y-4 mt-4">
              <Card>
                <CardHeader>
                  <CardTitle>Schedule Settings</CardTitle>
                  <CardDescription>
                    Configure when and how often this report should be generated
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Time Zone</Label>
                      <Select defaultValue="UTC">
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="UTC">UTC</SelectItem>
                          <SelectItem value="EST">Eastern Time</SelectItem>
                          <SelectItem value="PST">Pacific Time</SelectItem>
                          <SelectItem value="GMT">GMT</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Delivery Time</Label>
                      <Input type="time" defaultValue="09:00" />
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Auto-Send Reports</Label>
                      <p className="text-sm text-muted-foreground">
                        Automatically email reports to recipients when generated
                      </p>
                    </div>
                    <Switch defaultChecked />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Include Weekend Sessions</Label>
                      <p className="text-sm text-muted-foreground">
                        Include Saturday and Sunday data in weekly reports
                      </p>
                    </div>
                    <Switch defaultChecked />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReportTemplateEditor;